import React, { useState } from 'react';
import {
  Sparkles,
  Bot,
  ShieldCheck,
  Send,
  X,
  CheckCircle2,
  AlertCircle,
  Crown,
  Layers,
  Phone,
  Globe,
  Loader2,
  ExternalLink,
  Palette,
} from 'lucide-react';

export interface BespokeAvatarRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  authToken?: string;
  tenantName?: string;
  defaultRequestType?: 'bespoke_avatar' | 'white_label_rename' | 'full_package';
  onSuccess?: () => void;
}

export const BespokeAvatarRequestModal: React.FC<BespokeAvatarRequestModalProps> = ({
  isOpen,
  onClose,
  authToken,
  tenantName = 'Enterprise Client',
  defaultRequestType = 'bespoke_avatar',
  onSuccess,
}) => {
  const [requestType, setRequestType] = useState<'bespoke_avatar' | 'white_label_rename' | 'full_package'>(defaultRequestType);
  const [desiredAgentName, setDesiredAgentName] = useState('');
  const [avatarDescription, setAvatarDescription] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const res = await fetch('/api/tenet/request-custom-avatar', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          requestType,
          desiredAgentName,
          avatarDescription,
          referenceUrl,
          contactPhone,
          notes,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to submit request');
      }

      setIsSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setErrorMessage(err?.message || 'Connection error. Please try again or contact Master Admin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
      <div className="relative w-full max-w-xl bg-gradient-to-b from-[#0F172A] via-[#0B1120] to-[#060A14] border border-cyan-500/30 rounded-3xl shadow-[0_0_50px_rgba(6,182,212,0.25)] overflow-hidden text-slate-100 flex flex-col max-h-[90vh]">
        
        {/* Glowing Top Ambient Header */}
        <div className="relative p-6 border-b border-white/10 bg-gradient-to-r from-cyan-950/40 via-indigo-950/30 to-purple-950/40">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-5 right-5 p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition cursor-pointer"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-600 p-[1px] shadow-[0_0_20px_rgba(6,182,212,0.4)]">
              <div className="w-full h-full rounded-2xl bg-slate-950 flex items-center justify-center text-cyan-400">
                <Crown size={22} className="animate-pulse" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black text-white tracking-wide uppercase font-sans">
                  Premium White-Label & Bespoke 3D Avatar
                </h2>
                <span className="text-[9px] font-mono font-black px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  ENTERPRISE ADD-ON
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Elevate your brand with full white-label agent renaming and a bespoke 3D WebGL Avatar model.
              </p>
            </div>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {isSuccess ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-400 mx-auto flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                <CheckCircle2 size={32} />
              </div>
              <div>
                <h3 className="text-lg font-black text-white uppercase">Inquiry Submitted Successfully!</h3>
                <p className="text-xs text-slate-300 max-w-md mx-auto mt-2 leading-relaxed">
                  Our 3D Engineering & AI Architecture team has received your request for <span className="text-cyan-400 font-bold">{tenantName}</span>. A Master Admin representative will contact you via WhatsApp or Email within 24 hours with model preview and activation details.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider transition cursor-pointer shadow-lg mt-4"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && (
                <div className="p-3 rounded-xl bg-rose-950/50 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Service Selection Cards */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
                  Select Customization Tier
                </label>
                <div className="grid grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setRequestType('bespoke_avatar')}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      requestType === 'bespoke_avatar'
                        ? 'bg-cyan-500/15 border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                        : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Palette size={16} className={requestType === 'bespoke_avatar' ? 'text-cyan-400' : 'text-slate-400'} />
                    <span className="text-[11px] font-black text-white block mt-1.5 leading-tight">Bespoke 3D Avatar</span>
                    <span className="text-[9px] text-slate-400 block mt-0.5">Custom 3D Mascot / Face</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRequestType('white_label_rename')}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      requestType === 'white_label_rename'
                        ? 'bg-indigo-500/15 border-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                        : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Bot size={16} className={requestType === 'white_label_rename' ? 'text-indigo-400' : 'text-slate-400'} />
                    <span className="text-[11px] font-black text-white block mt-1.5 leading-tight">Agent Rebrand</span>
                    <span className="text-[9px] text-slate-400 block mt-0.5">Custom Brand & Identity</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRequestType('full_package')}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      requestType === 'full_package'
                        ? 'bg-purple-500/15 border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                        : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Crown size={16} className={requestType === 'full_package' ? 'text-purple-400' : 'text-slate-400'} />
                    <span className="text-[11px] font-black text-white block mt-1.5 leading-tight">Full Enterprise</span>
                    <span className="text-[9px] text-slate-400 block mt-0.5">Both Custom 3D & Rebrand</span>
                  </button>
                </div>
              </div>

              {/* Desired Agent Name Input */}
              {(requestType === 'white_label_rename' || requestType === 'full_package') && (
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
                    <Bot size={12} className="text-cyan-400" /> Desired Agent Brand Name
                  </label>
                  <input
                    type="text"
                    required
                    value={desiredAgentName}
                    onChange={(e) => setDesiredAgentName(e.target.value)}
                    placeholder="e.g. DTech AI Sales Concierge, Aura Assistant"
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 font-medium placeholder:text-slate-600"
                  />
                </div>
              )}

              {/* Avatar Description Input */}
              {(requestType === 'bespoke_avatar' || requestType === 'full_package') && (
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
                    <Palette size={12} className="text-cyan-400" /> 3D Avatar Concept & Requirements
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={avatarDescription}
                    onChange={(e) => setAvatarDescription(e.target.value)}
                    placeholder="Describe your desired 3D character, colors, clothing, mascot style, or specific GLB 3D model requirements..."
                    className="w-full bg-slate-900 border border-white/10 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 font-normal placeholder:text-slate-600 resize-none"
                  />
                </div>
              )}

              {/* Reference URL / Logo Link */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
                  <Globe size={12} className="text-cyan-400" /> Reference Image / Brand URL / 3D Model Link (Optional)
                </label>
                <input
                  type="url"
                  value={referenceUrl}
                  onChange={(e) => setReferenceUrl(e.target.value)}
                  placeholder="https://yourbrand.com/logo.png or Google Drive link"
                  className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 font-mono placeholder:text-slate-600"
                />
              </div>

              {/* Contact Phone Number */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
                  <Phone size={12} className="text-cyan-400" /> Direct WhatsApp / Contact Phone for Activation
                </label>
                <input
                  type="text"
                  required
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="+94 77 123 4567"
                  className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 font-mono placeholder:text-slate-600"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                <span className="text-[10px] text-slate-400 flex items-center gap-1">
                  <ShieldCheck size={13} className="text-emerald-400" />
                  Reviewed directly by Master Admin
                </span>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(6,182,212,0.3)] cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      <span>Submit Enterprise Request</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BespokeAvatarRequestModal;
